import { resolveImageUrl } from "../assets";
import { cardShareUrl } from "../engagement";
import { nativeShareFile } from "../native";
import { shareKit } from "../shareImage";
import type { InSnipCard } from "../types";
import type { Archetype } from "./archetype";
import type { Receipt } from "./receipts";

/**
 * v3 share renderers: the proof card (the receipt as a shareable artifact), the vertical
 * story card (Instagram and TikTok native 1080x1920), and the archetype card (the recap's
 * personality shareable). All compose the same Obsidian and Aurora Gold brand via shareKit.
 * House style: no em dashes or en dashes anywhere in copy.
 */

const { W, H, PAD, BG, INK, MUTED, ACCENT, SERIF, SANS, brandHost, fileSlug, loadImage, drawCover, wrapLines, roundRect } = shareKit;

const SW = 1080;
const SH = 1920;

export type ShareOutcome = "shared" | "downloaded" | "failed";

/** One share path for every v3 artifact: native sheet, web file share, then download. */
export async function shareBlob(
  blob: Blob | null,
  fileName: string,
  title: string,
  text: string,
): Promise<ShareOutcome> {
  if (!blob) return "failed";
  if (await nativeShareFile(blob, fileName, title, text)) return "shared";
  const file = new File([blob], fileName, { type: "image/png" });
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title, text });
      return "shared";
    } catch {
      return "failed"; // user cancelled
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

function newCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  return ctx ? [canvas, ctx] : null;
}

async function fontsReady(): Promise<void> {
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API unavailable, system fonts */
  }
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
    } catch {
      resolve(null); // tainted canvas or unsupported
    }
  });
}

function halo(ctx: CanvasRenderingContext2D, w: number, h: number, cy: number, strength = 0.14): void {
  const g = ctx.createRadialGradient(w / 2, cy, 0, w / 2, cy, w * 0.85);
  g.addColorStop(0, `rgba(240,198,104,${strength})`);
  g.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

function footer(ctx: CanvasRenderingContext2D, w: number, h: number, pad: number): void {
  const y = h - 92;
  ctx.textBaseline = "top";
  ctx.fillStyle = ACCENT;
  ctx.fillRect(pad, y - 26, 64, 5);
  ctx.textAlign = "left";
  ctx.font = `800 34px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", pad, y);
  ctx.textAlign = "right";
  ctx.font = `600 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(brandHost(), w - pad, y + 4);
}

const STRENGTH_LABEL: Record<Receipt["strength"], string> = {
  corroborated: "CORROBORATED",
  sourced: "TWO SOURCES",
  single: "ON THE RECORD",
};

/** The proof card: the story's receipt as a branded, screenshot-proof artifact. */
export async function renderProofImage(card: InSnipCard, receipt: Receipt): Promise<Blob | null> {
  const c = newCanvas(W, H);
  if (!c) return null;
  const [canvas, ctx] = c;
  await fontsReady();

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  halo(ctx, W, H, 320, 0.1);

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.font = `800 28px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText("THE RECEIPT", PAD, 128);
  ctx.textAlign = "right";
  ctx.font = `700 26px ${SANS}`;
  ctx.fillText(STRENGTH_LABEL[receipt.strength], W - PAD, 130);

  // The claim being backed.
  ctx.textAlign = "left";
  ctx.font = `400 62px ${SERIF}`;
  ctx.fillStyle = INK;
  let y = 208;
  for (const line of wrapLines(ctx, card.headline, W - PAD * 2, 4)) {
    ctx.fillText(line, PAD, y);
    y += 78;
  }
  y += 18;

  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, y, 64, 5);
  y += 42;

  const d = receipt.independentDomains;
  const n = receipt.sources.length;
  ctx.font = `700 34px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText(
    `Backed by ${n} source${n === 1 ? "" : "s"}${d > 1 ? ` across ${d} independent domains` : ""}`,
    PAD,
    y,
  );
  y += 74;

  // Each outlet, with its own headline where we have it (independent framing = the proof).
  for (const s of receipt.sources.slice(0, 3)) {
    const rowH = s.title ? 170 : 110;
    if (y + rowH > H - 210) break;
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    roundRect(ctx, PAD, y, W - PAD * 2, rowH, 18);
    ctx.fill();
    ctx.font = `800 32px ${SANS}`;
    ctx.fillStyle = ACCENT;
    ctx.fillText(`${s.primary ? "PRIMARY · " : ""}${s.name.toUpperCase().slice(0, 40)}`, PAD + 36, y + 30);
    if (s.title) {
      ctx.font = `400 33px ${SERIF}`;
      ctx.fillStyle = INK;
      let ty = y + 82;
      for (const line of wrapLines(ctx, `"${s.title}"`, W - PAD * 2 - 72, 2)) {
        ctx.fillText(line, PAD + 36, ty);
        ty += 42;
      }
    } else {
      ctx.font = `500 28px ${SANS}`;
      ctx.fillStyle = MUTED;
      ctx.fillText(s.host, PAD + 36, y + 66);
    }
    y += rowH + 22;
  }

  if (receipt.imageNote && y < H - 190) {
    ctx.font = `500 26px ${SANS}`;
    ctx.fillStyle = MUTED;
    ctx.fillText(`Image: ${receipt.imageNote}`, PAD, y + 6);
  }

  footer(ctx, W, H, PAD);
  return toBlob(canvas);
}

/** Share the proof card, with the story link in the caption. */
export async function shareProofImage(card: InSnipCard, receipt: Receipt): Promise<ShareOutcome> {
  const link = await cardShareUrl(card.id);
  const blob = await renderProofImage(card, receipt);
  const text = `The receipt behind "${card.headline}"\n\nRead it free on InSnip:\n${link}`;
  return shareBlob(blob, `insnip-receipt-${fileSlug(card.id)}.png`, "The receipt", text);
}

/** The vertical story card (1080x1920): photo up top melting into the brand, headline below. */
export async function renderStoryImage(card: InSnipCard): Promise<Blob | null> {
  const c = newCanvas(SW, SH);
  if (!c) return null;
  const [canvas, ctx] = c;
  await fontsReady();
  const pad = 96;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, SW, SH);

  const img = await loadImage(resolveImageUrl(card.imageUrl));
  if (img) {
    drawCover(ctx, img, 0, 0, SW, 1080);
    // Feather the photo into the obsidian below (the app's image-melt signature).
    const fade = ctx.createLinearGradient(0, 620, 0, 1090);
    fade.addColorStop(0, "rgba(11,10,14,0)");
    fade.addColorStop(1, BG);
    ctx.fillStyle = fade;
    ctx.fillRect(0, 600, SW, 500);
  } else {
    halo(ctx, SW, SH, 560, 0.12);
  }

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  let y = img ? 1120 : 560;
  ctx.font = `700 30px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText((card.category ?? "InSnip").toString().toUpperCase().slice(0, 40), pad, y);
  y += 58;

  ctx.font = `400 76px ${SERIF}`;
  ctx.fillStyle = INK;
  for (const line of wrapLines(ctx, card.headline, SW - pad * 2, 4)) {
    ctx.fillText(line, pad, y);
    y += 94;
  }
  y += 22;

  ctx.font = `400 36px ${SANS}`;
  ctx.fillStyle = MUTED;
  for (const line of wrapLines(ctx, card.essence || card.whyItMatters || "", SW - pad * 2, 4)) {
    ctx.fillText(line, pad, y);
    y += 52;
  }

  footer(ctx, SW, SH, pad);
  return toBlob(canvas);
}

/** Share the vertical story card (the Instagram and TikTok native shape). */
export async function shareStoryImage(card: InSnipCard): Promise<ShareOutcome> {
  const link = await cardShareUrl(card.id);
  const blob = await renderStoryImage(card);
  const text = `${card.headline}\n\nRead it free on InSnip:\n${link}`;
  return shareBlob(blob, `insnip-story-${fileSlug(card.id)}.png`, card.headline, text);
}

/** The archetype card: the reader's earned identity, in the story shape so it belongs
 *  on Instagram. A drawn emblem (gold ring + serif initials), never canvas emoji. */
export async function renderArchetypeImage(
  archetype: Archetype,
  monthLabel: string,
): Promise<Blob | null> {
  const c = newCanvas(SW, SH);
  if (!c) return null;
  const [canvas, ctx] = c;
  await fontsReady();
  const pad = 96;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, SW, SH);
  halo(ctx, SW, SH, 700, 0.16);

  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  ctx.font = `800 30px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(`YOUR SIGNAL · ${monthLabel.toUpperCase()}`, SW / 2, 220);

  // Emblem: a gold double ring holding the archetype's initials in the display serif.
  const cx = SW / 2;
  const cy = 560;
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, 170, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, 190, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
  const initials = archetype.name
    .replace(/^The\s+/i, "")
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  ctx.font = `400 150px ${SERIF}`;
  ctx.fillStyle = ACCENT;
  ctx.textBaseline = "middle";
  ctx.fillText(initials, cx, cy + 10);
  ctx.textBaseline = "top";

  ctx.font = `400 92px ${SERIF}`;
  ctx.fillStyle = INK;
  let y = 840;
  for (const line of wrapLines(ctx, archetype.name, SW - pad * 2, 2)) {
    ctx.fillText(line, SW / 2, y);
    y += 110;
  }
  y += 14;

  ctx.font = `400 40px ${SANS}`;
  ctx.fillStyle = MUTED;
  for (const line of wrapLines(ctx, archetype.line, SW - pad * 2 - 60, 3)) {
    ctx.fillText(line, SW / 2, y);
    y += 58;
  }
  y += 44;

  // The earned stat, in a quiet chip.
  ctx.font = `700 34px ${SANS}`;
  const statW = ctx.measureText(archetype.stat).width + 96;
  ctx.fillStyle = "rgba(240,198,104,0.12)";
  roundRect(ctx, (SW - statW) / 2, y, statW, 92, 46);
  ctx.fill();
  ctx.fillStyle = ACCENT;
  ctx.fillText(archetype.stat, SW / 2, y + 30);

  ctx.font = `500 30px ${SANS}`;
  ctx.fillStyle = MUTED;
  ctx.fillText("What kind of reader are you?", SW / 2, SH - 250);

  ctx.textAlign = "left";
  footer(ctx, SW, SH, pad);
  return toBlob(canvas);
}

/** Share the archetype card with an invite to find your own. */
export async function shareArchetypeImage(
  archetype: Archetype,
  monthLabel: string,
  refId: string,
): Promise<ShareOutcome> {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
  const blob = await renderArchetypeImage(archetype, monthLabel);
  // refId is the device id before sign-in and "" after it (an account uid never rides a link).
  const ref = refId ? `?r=${encodeURIComponent(refId)}` : "";
  const text =
    `My InSnip reader archetype: ${archetype.name}. ${archetype.line}\n` +
    `What kind of reader are you? ${origin}/${ref}`;
  return shareBlob(blob, `insnip-archetype-${archetype.id}.png`, archetype.name, text);
}
